import { api, cacheLongTimer } from "@app/api";
import { createSelector } from "@app/fx";
import { defaultEntity } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  DeployCost,
  DeployCostRates,
  DeployEnvironment,
  DeployStack,
} from "@app/types";
import { selectDiskAttachmentsByServiceId } from "../disk-attachment";
import {
  selectEnvironmentById,
  selectEnvironmentsByOrgAsList,
} from "../environment";
import { selectStacksByOrgAsList } from "../stack";

export * from "./calc";

interface CostResponse {
  id: number;
  est_cost: number;
  resource_type: string;
  _type: "cost";
}

type CostResourceType =
  | "Stack"
  | "Account"
  | "App"
  | "Database"
  | "Service"
  | "PersistentDisk";

// we need to create an ID in the UI that is different from what the API
// returns because the cost endpoints will returns ids for separate tables
// (e.g. stacks, accounts, apps, dbs, ...) which can have overlapping ids.
export function computeCostId(
  resourceType: CostResourceType,
  id: string,
): string {
  return `${resourceType}-${id}`;
}

function deserializeCost(c: CostResponse): DeployCost {
  return {
    id: computeCostId(c.resource_type as any, `${c.id}`),
    estCost: c.est_cost,
    resourceType: c.resource_type,
  };
}

const selectCosts = schema.costs.selectTable;
const selectCostRates = schema.costRates.select;

function selectServerCost(
  costs: ReturnType<typeof selectCosts>,
  resourceType: CostResourceType,
  id: string,
): number {
  return schema.costs.findById(costs, {
    id: computeCostId(resourceType, id),
  }).estCost;
}

function estimateBackupFloor(
  rates: DeployCostRates,
  totalBackupSize: number,
): number {
  return totalBackupSize * rates.backup_cost_gb_per_month;
}

function selectBackupFloor(
  rates: DeployCostRates,
  env: DeployEnvironment,
): number {
  return estimateBackupFloor(rates, env.totalBackupSize);
}

export function computeEffectiveEnvironmentCostValue(
  costs: ReturnType<typeof selectCosts>,
  rates: DeployCostRates,
  env: DeployEnvironment,
): number {
  const serverCost = selectServerCost(costs, "Account", env.id);
  const backupFloor = selectBackupFloor(rates, env);
  return Math.max(serverCost, backupFloor);
}

export function computeEffectiveStackCostValue(
  costs: ReturnType<typeof selectCosts>,
  rates: DeployCostRates,
  environments: DeployEnvironment[],
  id: string,
): number {
  const serverStackCost = selectServerCost(costs, "Stack", id);
  const missingBackupDelta = environments.reduce((total, env) => {
    if (env.stackId !== id) {
      return total;
    }

    const serverEnvCost = selectServerCost(costs, "Account", env.id);
    const backupFloor = selectBackupFloor(rates, env);
    return total + Math.max(backupFloor - serverEnvCost, 0);
  }, 0);

  return serverStackCost + missingBackupDelta;
}

interface OrgIdProp {
  orgId: string;
}

export const fetchCostsByStacks = api.get<OrgIdProp>("/costs/:orgId/stacks", {
  supervisor: cacheLongTimer(),
});

export const fetchCostsByEnvironments = api.get<OrgIdProp>(
  "/costs/:orgId/accounts",
  {
    supervisor: cacheLongTimer(),
  },
);

export const fetchCostsByApps = api.get<OrgIdProp>("/costs/:orgId/apps", {
  supervisor: cacheLongTimer(),
});

export const fetchCostsByDatabases = api.get<OrgIdProp>(
  "/costs/:orgId/databases",
  {
    supervisor: cacheLongTimer(),
  },
);

export const fetchCostsByServices = api.get<OrgIdProp>(
  "/costs/:orgId/services",
  {
    supervisor: cacheLongTimer(),
  },
);

export const fetchCostsByPersistentDisks = api.get<OrgIdProp>(
  "/costs/:orgId/persistent_disks",
  {
    supervisor: cacheLongTimer(),
  },
);

export const fetchCostRates = api.get<OrgIdProp, DeployCostRates>(
  "/costs/:orgId/rates",
  {
    supervisor: cacheLongTimer(),
  },
  function* (ctx, next) {
    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    yield* schema.update(schema.costRates.set(ctx.json.value));
  },
);

export const costEntities = {
  cost: defaultEntity({
    id: "cost",
    deserialize: deserializeCost,
    save: schema.costs.add,
  }),
};

// Sum of PersistentDisk cost rows for the disks attached to a service. Deprovisioned
// attachments are excluded; a disk attached at multiple mount points contributes once.
export const selectPersistentDiskCostByService = createSelector(
  selectCosts,
  selectDiskAttachmentsByServiceId,
  (costs, attachments) => {
    const diskIds = new Set(
      attachments
        .filter((a) => a.status !== "deprovisioned")
        .map((a) => a.persistentDiskId),
    );
    let total = 0;
    for (const diskId of diskIds) {
      const cost = schema.costs.findById(costs, {
        id: computeCostId("PersistentDisk", diskId),
      });
      total += cost.estCost;
    }
    return total;
  },
);

export const selectEffectiveEnvironmentCost = createSelector(
  selectCosts,
  selectCostRates,
  selectEnvironmentById,
  (costs, rates, env) =>
    computeEffectiveEnvironmentCostValue(costs, rates, env),
);

export const selectEffectiveStackCost = createSelector(
  selectCosts,
  selectCostRates,
  selectEnvironmentsByOrgAsList,
  (_: WebState, p: { id: string }) => p.id,
  (costs, rates, environments, id) =>
    computeEffectiveStackCostValue(costs, rates, environments, id),
);

export const formatCurrency = (num?: number, decimalPlaces = 2) =>
  (num || 0.0).toLocaleString("en", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimalPlaces,
  });

export const formatRate = (num?: number) => {
  if (Number.isNaN(num)) return "$0.00";
  const value = num || 0.0;
  // Format with at least 2 decimal places, up to 8 decimal places
  return value.toLocaleString("en", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });
};

// Selector: total monthly cost across stacks in the org
export const selectTotalMonthlyCostByStacks = createSelector(
  selectStacksByOrgAsList,
  selectCosts,
  selectCostRates,
  selectEnvironmentsByOrgAsList,
  (stacks, costs, rates, environments) => {
    if (!stacks || !costs) return 0;
    return stacks.reduce((total: number, stack: DeployStack) => {
      return (
        total +
        computeEffectiveStackCostValue(costs, rates, environments, stack.id)
      );
    }, 0);
  },
);
